import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Star } from "lucide-react";
import api from "@/lib/api";
import { useSiteLoaded, useSiteText } from "@/lib/siteContent";

export const parsePicks = (raw) => { try { const v = JSON.parse(raw || "[]"); return Array.isArray(v) ? v : []; } catch { return []; } };

export const stripReviews = (all, mode, picks) => {
  const real = all.filter((r) => r.body && r.source !== "seed" && r.review_id !== "g_business_photos");
  if (mode === "picked") return real.filter((r) => picks.includes(r.review_id));
  if (mode === "all") return real.slice(0, 10);
  return real.filter((r) => r.rating >= 5).slice(0, 10);
};

// Rotating strip of review highlights right under the homepage car
export const ReviewStrip = () => {
  const site = useSiteText();
  const loaded = useSiteLoaded();
  const [all, setAll] = useState([]);
  const [i, setI] = useState(0);
  const mode = site("reviews.strip_mode") || "five";
  useEffect(() => { api.get("/reviews?limit=30").then((r) => setAll(r.data.reviews || [])).catch(() => {}); }, []);
  const items = stripReviews(all, mode, parsePicks(site("reviews.strip_picks")));
  useEffect(() => {
    if (items.length < 2) return undefined;
    const t = setInterval(() => setI((x) => (x + 1) % items.length), 6000);
    return () => clearInterval(t);
  }, [items.length]);
  if (!loaded || mode === "off" || !items.length) return null;
  const r = items[i % items.length];
  const avg = (items.reduce((s, x) => s + (x.rating || 5), 0) / items.length).toFixed(1);
  return (
    <section className="relative border-y border-white/5 bg-[#05070a]" data-testid="review-strip">
      <div className="max-w-5xl mx-auto px-6 py-6 min-h-[112px] flex items-center gap-5">
        <div className="hidden sm:flex flex-col items-center shrink-0">
          <span className="text-3xl font-['Outfit'] text-white" data-testid="review-strip-avg">{avg}</span>
          <span className="text-[10px] uppercase tracking-[0.2em] text-gray-500">{r.source === "google" ? "Google" : "Verified"}</span>
        </div>
        <AnimatePresence mode="wait">
          <motion.figure key={r.review_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.45 }} className="flex-1 min-w-0" data-testid="review-strip-item">
            <div className="flex items-center gap-1 mb-1.5">{[...Array(r.rating || 5)].map((_, k) => <Star key={k} className="w-3.5 h-3.5 fill-sky-400 text-sky-400" />)}</div>
            <blockquote className="text-sm md:text-base text-gray-200 line-clamp-2">“{r.body}”</blockquote>
            <figcaption className="text-xs text-gray-500 mt-1.5" data-testid="review-strip-author">— {r.author_name}</figcaption>
          </motion.figure>
        </AnimatePresence>
        {items.length > 1 && (
          <div className="flex gap-1.5 shrink-0">
            {items.map((x, k) => <button key={x.review_id} onClick={() => setI(k)} aria-label={`Review ${k + 1}`} className={`h-1.5 rounded-full transition-[width,background-color] duration-300 ${k === i % items.length ? "w-5 bg-sky-400" : "w-1.5 bg-white/20"}`} data-testid={`review-strip-dot-${k}`} />)}
          </div>
        )}
      </div>
    </section>
  );
};
